import { memo, useMemo, useState, useCallback, useEffect, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Sankey from 'highcharts/modules/sankey';
import ErrorBoundary from '../../ErrorBoundary/ErrorBoundary';
import SegmentedToggle from './SegmentedToggle';
import PeriodTimeline from './PeriodTimeline';
import DisclaimerMarker from './DisclaimerMarker';
import SankeyNodePopover from './SankeyNodePopover';
import { buildIncomeSankeyModel } from '../utils/incomeStatementSankey';
import { getIncomeStatementSankeyOptions, NODE_COLORS } from '../config/incomeStatementSankeyChartConfig';
import styles from './IncomeStatementSankeyCard.module.css';

// The Sankey series type ships as an optional Highcharts module. Register it
// once, at module load, against the SAME `'highcharts'` instance this card
// renders with — NOT the `highcharts/highstock` instance Analysis.js registers
// HighchartsMore on (a different module object; registering there would leave
// this instance without the sankey type and the chart would throw).
if (typeof Sankey === 'function') Sankey(Highcharts);

const CADENCE_OPTIONS = [
  { value: 'annual', label: 'Annual' },
  { value: 'quarterly', label: 'Quarterly' },
];

// Generate distinct shades for pie slices that share the same base color.
// Keeps hue fixed, spreads lightness so identical-colored siblings look different.
const hexToHsl = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h, s, l];
};

const hslToHex = (h, s, l) => {
  const hue2rgb = (p, q, t) => {
    const tt = t < 0 ? t + 1 : t > 1 ? t - 1 : t;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  let r, g, b;
  if (s === 0) { r = g = b = l; }
  else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  const toHex = (v) => Math.round(v * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const assignDistinctColors = (items) => {
  const groups = {};
  items.forEach((item, i) => {
    (groups[item.baseColor] = groups[item.baseColor] || []).push(i);
  });
  return items.map((item, i) => {
    const siblings = groups[item.baseColor];
    let color;
    if (siblings.length === 1) {
      color = item.baseColor;
    } else {
      const [h, s, baseL] = hexToHsl(item.baseColor);
      const idx = siblings.indexOf(i);
      const span = Math.min(0.35, 0.12 * siblings.length);
      const l = baseL - span / 2 + (span * idx) / (siblings.length - 1);
      color = hslToHex(h, s, Math.max(0.2, Math.min(0.75, l)));
    }
    return { name: item.name, weight: item.weight, color };
  });
};

/**
 * Dumb card: visualizes one period's income statement as a Highcharts Sankey
 * (revenue -> costs/gross profit -> operating expenses/operating income ->
 * pre-tax -> interest/taxes/net income), with an Annual/Quarterly toggle and a
 * period timeline scrubber (plus prev/next arrows). All derivation lives in
 * `buildIncomeSankeyModel`; all selection state lives in Analysis.js — this
 * component only reads props and renders. Figures are shown in the statement's
 * reporting `currency` (not FX-converted).
 */
const IncomeStatementSankeyCard = ({
  periods = [], currency, cadence = 'annual', onCadenceChange = () => {},
  index = 0, onSelect = () => {}, onPrev = () => {}, onNext = () => {}, symbol, embedded = false,
}) => {
  const hasPeriods = Array.isArray(periods) && periods.length > 0;
  const safeIndex = hasPeriods ? Math.min(index, periods.length - 1) : 0;
  const active = hasPeriods ? periods[safeIndex] : null;

  const model = useMemo(() => buildIncomeSankeyModel(active), [active]);

  // Hovered-node popover state: { node, breakdown } or null.
  const [popoverData, setPopoverData] = useState(null);

  // Dismiss the popover when the period (model) changes.
  useEffect(() => { setPopoverData(null); }, [model]);

  const handleNodeHover = useCallback((nodeId) => {
    if (dismissTimer.current) { clearTimeout(dismissTimer.current); dismissTimer.current = null; }
    const node = model.nodes.find((n) => n.id === nodeId);
    if (!node) { setPopoverData(null); return; }
    const outbound = model.links.filter((l) => l.from === nodeId);
    if (outbound.length === 0) { setPopoverData(null); return; }
    const kindById = {};
    model.nodes.forEach((n) => { kindById[n.id] = n.kind; });
    const nameById = {};
    model.nodes.forEach((n) => { nameById[n.id] = n.name; });
    const valueById = {};
    model.nodes.forEach((n) => { valueById[n.id] = n.value; });
    const allTokens = outbound.every((l) => l.flow === 'token');
    const raw = outbound.map((l) => {
      const useRealValue = allTokens || l.flow !== 'token';
      const realValue = useRealValue ? Math.abs(valueById[l.to] || 0) : 0;
      return {
        name: nameById[l.to] || l.to,
        weight: realValue || l.weight,
        baseColor: NODE_COLORS[kindById[l.to]] || NODE_COLORS.profit,
      };
    });
    const breakdown = assignDistinctColors(raw);
    setPopoverData({ node, breakdown });
  }, [model]);

  const dismissTimer = useRef(null);
  const handlePopoverClose = useCallback(() => {
    dismissTimer.current = setTimeout(() => setPopoverData(null), 200);
  }, []);
  const handlePopoverEnter = useCallback(() => {
    if (dismissTimer.current) { clearTimeout(dismissTimer.current); dismissTimer.current = null; }
  }, []);
  const handlePopoverLeave = useCallback(() => setPopoverData(null), []);

  const options = useMemo(
    () => getIncomeStatementSankeyOptions(model, {
      currency,
      onNodeClick: handleNodeHover, onNodeOut: handlePopoverClose,
    }),
    [model, currency, handleNodeHover, handlePopoverClose],
  );

  const inner = (
    <>
      <div className={`${styles.header}${embedded ? ` ${styles.headerEmbedded}` : ''}`}>
        <div className={styles.titleGroup}>
          {!embedded && (
            <h2 className={styles.title}>
              Income Flow
              <DisclaimerMarker id="income-statement-sankey" />
            </h2>
          )}
          {currency && (
            <span className={styles.currencyLabel}>
              Amounts in {currency}
              {embedded && <DisclaimerMarker id="income-statement-sankey" />}
            </span>
          )}
        </div>
        <SegmentedToggle
          options={CADENCE_OPTIONS}
          value={cadence}
          onChange={onCadenceChange}
          ariaLabel="Statement cadence"
        />
      </div>

      {!hasPeriods ? (
        <div className={styles.placeholder}>Income statement unavailable for {symbol}.</div>
      ) : (
        <>
          <div className={styles.periodRow}>
            <button
              type="button"
              className={styles.stepper}
              onClick={onPrev}
              disabled={safeIndex <= 0}
              aria-label="Previous period"
            >
              &#8249;
            </button>
            <PeriodTimeline
              periods={periods}
              index={safeIndex}
              onSelect={onSelect}
              cadence={cadence}
              ariaLabel="Statement period"
            />
            <button
              type="button"
              className={styles.stepper}
              onClick={onNext}
              disabled={safeIndex >= periods.length - 1}
              aria-label="Next period"
            >
              &#8250;
            </button>
          </div>

          {model.links.length === 0 ? (
            <div className={styles.placeholder}>Not enough data to chart this period.</div>
          ) : (
            <div className={styles.chartWrapper}>
              <ErrorBoundary message="Chart couldn't render.">
                <HighchartsReact highcharts={Highcharts} options={options} immutable />
              </ErrorBoundary>
              {popoverData && (
                <SankeyNodePopover
                  node={popoverData.node}
                  breakdown={popoverData.breakdown}
                  currency={currency}
                  onMouseEnter={handlePopoverEnter}
                  onMouseLeave={handlePopoverLeave}
                />
              )}
              {model.notes.length > 0 && (
                <div className={styles.chartNotes}>
                  {model.notes.join(' · ')}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );

  return embedded ? inner : <section className={styles.card}>{inner}</section>;
};

export default memo(IncomeStatementSankeyCard);
